import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Clock, AlertCircle, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Admin form state
  const [adminForm, setAdminForm] = useState({ email: '', password: '' });
  const [adminLoading, setAdminLoading] = useState(false);
  
  // User form state
  const [userForm, setUserForm] = useState({ email: '', password: '' });
  const [userLoading, setUserLoading] = useState(false);
  
  const [pendingMessage, setPendingMessage] = useState(null);

  // Check for deactivated message from redirect
  useEffect(() => {
    if (location.state?.deactivatedMessage) {
      setPendingMessage({ type: 'deactivated', message: location.state.deactivatedMessage });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    setPendingMessage(null);
    try {
      const user = await login(adminForm.email, adminForm.password);
      if (user.role !== 'admin') {
        toast.error('This section is for Admin login only. Please use the Users section on the right.');
        // Don't navigate - stay on login page
        return;
      }
      toast.success('Admin login successful!');
      navigate(`/${user.role}`);
    } catch (error) {
      handleLoginError(error);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setUserLoading(true);
    setPendingMessage(null);
    try {
      const user = await login(userForm.email, userForm.password);
      if (user.role === 'admin') {
        toast.error('Admin accounts must login from the Admin section on the left.');
        // Don't navigate - stay on login page
        return;
      }
      toast.success('Login successful!');
      navigate(`/${user.role}`);
    } catch (error) {
      handleLoginError(error);
    } finally {
      setUserLoading(false);
    }
  };

  const handleLoginError = (error) => {
    let detail = error.response?.data?.detail || 'Login failed';
    
    if (typeof detail !== 'string') {
      if (Array.isArray(detail)) {
        detail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (typeof detail === 'object') {
        detail = detail.msg || detail.message || JSON.stringify(detail);
      }
    }
    
    if (detail.includes('pending admin approval')) {
      setPendingMessage({ type: 'admin', message: detail });
    } else if (detail.includes('pending resident approval')) {
      setPendingMessage({ type: 'resident', message: detail });
    } else if (detail.includes('deactivated')) {
      setPendingMessage({ type: 'deactivated', message: detail });
    } else {
      toast.error(detail);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex justify-center py-6">
        <button
          onClick={() => navigate('/home')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          data-testid="home-logo-btn"
        >
          <div className="h-12 w-12 bg-primary rounded-xl flex items-center justify-center">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>FortiHome</span>
        </button>
      </div>

      {/* Pending message alert - shown above both forms */}
      {pendingMessage && (
        <div className="max-w-4xl mx-auto px-4 mb-4 w-full">
          <Alert className={`${
            pendingMessage.type === 'deactivated' 
              ? 'border-red-200 bg-red-50' 
              : 'border-amber-200 bg-amber-50'
          }`}>
            {pendingMessage.type === 'deactivated' ? (
              <AlertCircle className="h-4 w-4 text-red-600" />
            ) : (
              <Clock className="h-4 w-4 text-amber-600" />
            )}
            <AlertDescription className={
              pendingMessage.type === 'deactivated' ? 'text-red-700' : 'text-amber-700'
            }>
              {pendingMessage.message}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main Content - Split Layout */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8">
          
          {/* Left Side - Admin Login */}
          <Card className="shadow-xl border-slate-200 bg-white">
            <CardHeader className="space-y-4 pb-4">
              <div className="flex justify-center">
                <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-center text-slate-900" style={{ fontFamily: 'Manrope' }}>Admin Portal</CardTitle>
              <CardDescription className="text-center text-slate-500">Society administrators only</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="admin@fortihome.com"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    required
                    data-testid="admin-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    placeholder="••••••••"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    required
                    data-testid="admin-password-input"
                    className="h-11"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-primary hover:bg-primary/90" 
                  disabled={adminLoading} 
                  data-testid="admin-login-btn"
                >
                  {adminLoading ? 'Signing in...' : 'Admin Sign In'}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-4">
                For society management access only
              </p>
            </CardContent>
          </Card>

          {/* Right Side - User Login */}
          <Card className="shadow-xl border-slate-200 bg-white">
            <CardHeader className="space-y-4 pb-4">
              <div className="flex justify-center">
                <div className="h-16 w-16 bg-secondary/10 rounded-2xl flex items-center justify-center">
                  <Users className="h-8 w-8 text-secondary" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-center text-slate-900" style={{ fontFamily: 'Manrope' }}>User Login</CardTitle>
              <CardDescription className="text-center text-slate-500">Residents, Tenants & Security</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUserSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    placeholder="your@email.com"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password">Password</Label>
                  <Input
                    id="user-password"
                    type="password"
                    placeholder="••••••••"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required
                    data-testid="user-password-input"
                    className="h-11"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-secondary hover:bg-secondary/90" 
                  disabled={userLoading} 
                  data-testid="user-login-btn"
                >
                  {userLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-slate-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-secondary font-semibold hover:underline" data-testid="login-register-link">
                    Register here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
